/**
 * Prénoms et noms : une majuscule en tête de chaque mot, en quittant le champ.
 *
 * Même règle que la base (fonction `nom_majuscule_initiale`, migration
 * `zzzzzzzzzzzzz_noms_majuscule_initiale.sql`), qui l'impose à l'écriture ;
 * ici, la personne voit le résultat avant d'enregistrer. Si les deux
 * divergent, c'est la base qui a raison : corriger celle-ci pour la suivre.
 *
 * - majuscule en tête de chaque mot, et après un tiret ou une apostrophe ;
 * - un mot mélangé garde ses autres lettres (« mcDonald » → « McDonald ») ;
 * - un mot tout en majuscules (2 lettres ou plus) repasse en minuscules avant
 *   sa majuscule (« DUPONT » → « Dupont », « VAN DER BERG » → « van der Berg ») ;
 * - dans un nom de famille, les particules restent telles que tapées
 *   (« van der berg » → « van der Berg »), sauf en dernier mot (« le » → « Le »).
 */
const PARTICULES = new Set([
  'de', 'du', 'des', 'd', 'la', 'le', 'les', 'l',
  'van', 'von', 'vom', 'der', 'den', 'ter', 'ten', 'te', 't', 'het',
  'zu', 'zum', 'zur', 'da', 'di', 'do', 'dos', 'das', 'del', 'della',
  'dei', 'degli', 'delle', 'y', 'e', 'al', 'el', 'ben', 'bin', 'ibn',
  'af', 'av',
]);
const SEPARATEUR = /^[ \-'’]+$/;

export function nomMajusculeInitiale(valeur: string, nomDeFamille: boolean): string {
  const mots = valeur.trim().replace(/\s+/g, ' ').match(/[^ \-'’]+|[ \-'’]+/g);
  if (!mots) return '';
  let dernier = -1;
  mots.forEach((m, i) => { if (!SEPARATEUR.test(m)) dernier = i; });
  return mots.map((brut, i) => {
    if (SEPARATEUR.test(brut)) return brut;
    const toutMajuscules = /\p{L}.*\p{L}/u.test(brut) && brut === brut.toUpperCase() && brut !== brut.toLowerCase();
    const m = toutMajuscules ? brut.toLowerCase() : brut;
    if (nomDeFamille && i !== dernier && PARTICULES.has(m.toLowerCase())) return m;
    return m.charAt(0).toUpperCase() + m.slice(1);
  }).join('');
}

/** Prénom ou nom de famille, d'après `data-nom`, sinon l'`id` ou le `name` du champ. */
function genreDuChamp(el: HTMLInputElement): 'prenom' | 'famille' | null {
  const explicite = el.dataset.nom;
  if (explicite === 'prenom' || explicite === 'famille') return explicite;
  const cle = `${el.id} ${el.name}`;
  if (/first_?name/i.test(cle)) return 'prenom';
  if (/last_?name/i.test(cle)) return 'famille';
  return null;
}

export function installNameCase(): () => void {
  // Pour qu'une valeur posée par programme soit vue par React, il faut passer
  // par le setter natif puis émettre un événement `input`. Lu ici et pas au
  // chargement du module : Next rend aussi ce module côté serveur, où
  // `HTMLInputElement` n'existe pas (2026-09-28, staging en 500).
  const setterNatif = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  const handler = (event: Event) => {
    const el = event.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'text' && el.type !== '') return;
    const genre = genreDuChamp(el);
    if (!genre) return;
    const corrige = nomMajusculeInitiale(el.value, genre === 'famille');
    if (corrige === el.value || !setterNatif) return;
    setterNatif.call(el, corrige);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  };
  // focusout en capture : passe avant les onBlur de React, qui enregistrent.
  document.addEventListener('focusout', handler, true);
  return () => document.removeEventListener('focusout', handler, true);
}
